import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, MapPin, X, Repeat, CloudRain, CalendarPlus } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { ICS_URL } from "@/lib/calendar";

const empty = { park_name: "", address: "", city: "Salt Lake City, UT", lat: 0, lng: 0, date: "", time: "", duration_min: 45, price_app: 40, price_online: 45, capacity: 15, notes: "", active: true };

export default function DashSchedule() {
  const [sessions, setSessions] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/park-sessions").then((r) => setSessions(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (k, num) => (e) => setForm({ ...form, [k]: num ? Number(e.target.value) : e.target.value });

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/park-sessions/${editing}`, form);
      else await api.post("/park-sessions", form);
      toast.success(editing ? "Session updated" : "Session added");
      setOpen(false); setForm(empty); setEditing(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const del = async (id) => { await api.delete(`/park-sessions/${id}`); toast.success("Session removed"); load(); };
  const edit = (s) => { setForm({ ...s }); setEditing(s.id); setOpen(true); };

  const generate = async () => {
    try {
      const { data } = await api.post("/park-sessions/generate", {});
      toast.success(`Generated ${data.created} weekly sessions (Mon Sugar House · Wed Liberty · Sat Tanner).`);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const toggleWeather = async (id) => {
    try { const { data } = await api.post(`/park-sessions/${id}/weather-hold`); toast.success(data.weather_hold ? "Weather hold on" : "Weather hold cleared"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const bump = async (id) => {
    try { await api.post(`/park-sessions/${id}/bump`, { days: 1 }); toast.success("Session moved +1 day"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const copyIcs = () => { navigator.clipboard.writeText(ICS_URL); toast.success("Calendar feed URL copied — paste into Google Calendar ▸ Other calendars ▸ From URL"); };

  return (
    <div data-testid="dash-schedule-page">
      <div className="flex items-center justify-between">
        <div><h1 className="font-display text-4xl">Park Schedule</h1><p className="text-brand-ink/60 mt-2">Publish where and when you'll teach.</p></div>
        <div className="flex gap-3 flex-wrap">
          <button onClick={generate} className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-brand-line font-medium hover:border-brand-red hover:text-brand-red transition-colors" data-testid="generate-schedule-btn"><Repeat className="w-4 h-4" /> Auto Mon/Wed/Sat</button>
          <button onClick={copyIcs} className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-brand-line font-medium hover:border-brand-red hover:text-brand-red transition-colors" data-testid="copy-ics-btn"><CalendarPlus className="w-4 h-4" /> Google Calendar feed</button>
          <button onClick={() => { setForm(empty); setEditing(null); setOpen(true); }} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand-red text-white font-medium hover:bg-brand-reddark transition-colors" data-testid="add-session-btn"><Plus className="w-4 h-4" /> Add session</button>
        </div>
      </div>

      <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {sessions.length === 0 && <p className="text-brand-ink/50" data-testid="schedule-empty-admin">No sessions yet. Add your first park session.</p>}
        {sessions.map((s) => (
          <div key={s.id} className="bg-white rounded-2xl border border-brand-line p-6" data-testid={`admin-session-${s.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2 text-brand-red font-medium"><MapPin className="w-4 h-4" /> {s.park_name}</div>
              <button onClick={() => del(s.id)} className="text-brand-ink/30 hover:text-brand-red transition-colors" data-testid={`delete-session-${s.id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
            <div className="mt-3 font-display text-2xl">{s.date}</div>
            <div className="text-sm text-brand-ink/60 mt-1">{s.time} · {s.duration_min} min · {s.capacity} spots</div>
            <div className="text-sm text-brand-ink/50 mt-1">${s.price_app} app / ${s.price_online} online</div>
            <div className="mt-3 flex gap-2 flex-wrap">
              {s.weather_hold && <span className="text-xs font-semibold text-amber-700 bg-amber-100 px-2.5 py-1 rounded-full">Weather hold</span>}
              {s.is_opener && <span className="text-xs font-semibold text-brand-red bg-brand-mist px-2.5 py-1 rounded-full">Opener</span>}
            </div>
            <div className="mt-4 flex items-center gap-4 text-sm flex-wrap">
              <button onClick={() => edit(s)} className="text-brand-ink hover:text-brand-red transition-colors" data-testid={`edit-session-${s.id}`}>Edit</button>
              <button onClick={() => toggleWeather(s.id)} className={`inline-flex items-center gap-1 transition-colors ${s.weather_hold ? "text-amber-700" : "text-brand-ink/60 hover:text-brand-red"}`} data-testid={`weather-hold-btn-${s.id}`}><CloudRain className="w-4 h-4" /> {s.weather_hold ? "Clear" : "Weather"}</button>
              <button onClick={() => bump(s.id)} className="inline-flex items-center gap-1 text-brand-ink/60 hover:text-brand-red transition-colors" data-testid={`bump-btn-${s.id}`}><CalendarPlus className="w-4 h-4" /> +1 day</button>
            </div>
          </div>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="session-modal">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-auto p-8">
            <div className="flex items-center justify-between mb-6"><h2 className="font-display text-2xl">{editing ? "Edit" : "New"} session</h2><button onClick={() => setOpen(false)}><X className="w-5 h-5" /></button></div>
            <form onSubmit={save} className="grid grid-cols-2 gap-4">
              <In label="Park name" v={form.park_name} on={set("park_name")} req span testid="session-park" />
              <In label="Address" v={form.address} on={set("address")} span testid="session-address" />
              <In label="Date" type="date" v={form.date} on={set("date")} req testid="session-date" />
              <In label="Time" type="time" v={form.time} on={set("time")} req testid="session-time" />
              <In label="Duration (min)" type="number" v={form.duration_min} on={set("duration_min", true)} testid="session-duration" />
              <In label="Capacity" type="number" v={form.capacity} on={set("capacity", true)} testid="session-capacity" />
              <In label="Price (app)" type="number" v={form.price_app} on={set("price_app", true)} testid="session-price-app" />
              <In label="Price (online)" type="number" v={form.price_online} on={set("price_online", true)} testid="session-price-online" />
              <In label="Latitude" type="number" v={form.lat} on={set("lat", true)} testid="session-lat" />
              <In label="Longitude" type="number" v={form.lng} on={set("lng", true)} testid="session-lng" />
              <div className="col-span-2"><label className="text-xs uppercase tracking-[0.2em] text-brand-ink/50 font-semibold">Notes</label><textarea value={form.notes} onChange={set("notes")} rows={2} className="mt-2 w-full rounded-xl border border-brand-line px-4 py-2.5 focus:ring-2 focus:ring-brand-red focus:outline-none" data-testid="session-notes" /></div>
              <button type="submit" className="col-span-2 px-6 py-3 rounded-full bg-brand-red text-white font-medium hover:bg-brand-reddark transition-colors" data-testid="session-save-btn">{editing ? "Save changes" : "Add session"}</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function In({ label, type = "text", v, on, req, span, testid }) {
  return (
    <div className={span ? "col-span-2" : ""}>
      <label className="text-xs uppercase tracking-[0.2em] text-brand-ink/50 font-semibold">{label}</label>
      <input type={type} value={v} onChange={on} required={req} className="mt-2 w-full rounded-xl border border-brand-line px-4 py-2.5 focus:ring-2 focus:ring-brand-red focus:outline-none" data-testid={testid} />
    </div>
  );
}
